import { Container, Graphics, Text } from "pixi.js";
import { P } from "./palette";

// Простые векторные иллюстрации в едином стиле: мягкие округлые формы,
// тёплая палитра, деликатный «контур» (полупрозрачная тёмная обводка),
// белые глаза с точкой-зрачком и бликом. Не эмодзи, не production-арт —
// цельный прототип-визуал под референс.

const OUTLINE = { color: P.ink, width: 3, alpha: 0.22 } as const;

function eye(g: Container, x: number, y: number, r: number, look = 0) {
  const w = new Graphics().circle(x, y, r).fill(0xffffff).stroke(OUTLINE);
  const p = new Graphics().circle(x + look, y + r * 0.15, r * 0.52).fill(P.ink);
  const s = new Graphics().circle(x + look - r * 0.25, y - r * 0.3, r * 0.22).fill(0xffffff);
  g.addChild(w, p, s);
}

export interface Sprite {
  node: Container;
  parts: Record<string, Container | Graphics>;
}

// --- Кукушка ------------------------------------------------------------
export function makeCuckoo(): Sprite {
  const node = new Container();
  const body = new Graphics()
    .ellipse(0, 0, 34, 40)
    .fill(P.cuckooBody)
    .stroke(OUTLINE);
  const belly = new Graphics().ellipse(2, 8, 20, 24).fill(0xfff7e8);
  const wing = new Graphics()
    .moveTo(-4, -10)
    .quadraticCurveTo(-34, -2, -20, 26)
    .quadraticCurveTo(-8, 16, -4, -10)
    .fill(P.cuckooWing)
    .stroke(OUTLINE);
  const head = new Graphics().circle(6, -34, 22).fill(P.cuckooHead).stroke(OUTLINE);
  const tuft = new Graphics()
    .moveTo(2, -54)
    .quadraticCurveTo(-6, -70, 8, -64)
    .quadraticCurveTo(6, -74, 18, -60)
    .fill(P.cuckooWing);
  const beakClosed = new Graphics().poly([24, -34, 44, -30, 24, -24]).fill(P.beak).stroke(OUTLINE);
  const beakOpen = new Graphics()
    .poly([24, -36, 46, -34, 26, -30])
    .fill(P.beak)
    .stroke(OUTLINE);
  const beakOpenLow = new Graphics().poly([25, -28, 45, -24, 26, -20]).fill(0xd07f1f);
  const beakOpenGrp = new Container();
  beakOpenGrp.addChild(beakOpen, beakOpenLow);
  beakOpenGrp.visible = false;
  const tail = new Graphics().poly([-24, 20, -44, 34, -22, 34]).fill(P.cuckooWing).stroke(OUTLINE);
  const feet = new Graphics()
    .moveTo(-8, 38).lineTo(-8, 50).moveTo(-14, 50).lineTo(0, 50)
    .moveTo(10, 38).lineTo(10, 50).moveTo(4, 50).lineTo(18, 50)
    .stroke({ color: P.beak, width: 4 });
  node.addChild(tail, wing, body, belly, feet, head, tuft, beakClosed, beakOpenGrp);
  eye(node, 12, -36, 7, 1);
  const blink = new Graphics().rect(3, -40, 20, 8).fill(P.cuckooHead);
  blink.visible = false;
  node.addChild(blink);
  return { node, parts: { head, beakClosed, beakOpenGrp, blink, wing } };
}

// --- Лягушка ----------------------------------------------------------
export function makeFrog(): Sprite {
  const node = new Container();
  const legs = new Graphics()
    .ellipse(-22, 14, 14, 9).ellipse(22, 14, 14, 9)
    .fill(P.frogDark).stroke(OUTLINE);
  const body = new Graphics().ellipse(0, 0, 30, 24).fill(P.frog).stroke(OUTLINE);
  const belly = new Graphics().ellipse(0, 8, 20, 13).fill(P.frogBelly);
  const eyeL = new Graphics().circle(-13, -22, 12).fill(P.frog).stroke(OUTLINE);
  const eyeR = new Graphics().circle(13, -22, 12).fill(P.frog).stroke(OUTLINE);
  const feetF = new Graphics()
    .ellipse(-16, 20, 10, 6).ellipse(16, 20, 10, 6)
    .fill(P.frogDark);
  const smile = new Graphics()
    .moveTo(-16, 4).quadraticCurveTo(0, 18, 16, 4)
    .stroke({ color: P.ink, width: 3, alpha: 0.5 });
  const smileWide = new Graphics()
    .moveTo(-18, 2).quadraticCurveTo(0, 24, 18, 2)
    .quadraticCurveTo(0, 12, -18, 2)
    .fill(0x8a3b3b);
  smileWide.visible = false;
  node.addChild(legs, body, belly, feetF, smile, smileWide, eyeL, eyeR);
  eye(node, -13, -24, 6, 1);
  eye(node, 13, -24, 6, 1);
  return { node, parts: { smile, smileWide } };
}

// --- Улитка ----------------------------------------------------------
export function makeSnail(): Sprite {
  const node = new Container();
  const foot = new Graphics()
    .moveTo(-34, 10)
    .quadraticCurveTo(-38, -6, -20, -8)
    .lineTo(20, -8)
    .quadraticCurveTo(40, -8, 34, 10)
    .quadraticCurveTo(0, 20, -34, 10)
    .fill(P.snailBody)
    .stroke(OUTLINE);
  const head = new Graphics().ellipse(-30, -6, 12, 10).fill(P.snailBody).stroke(OUTLINE);
  const hornL = new Graphics().moveTo(-34, -12).lineTo(-40, -28).stroke({ color: P.snailBody, width: 4 });
  const hornR = new Graphics().moveTo(-28, -12).lineTo(-30, -30).stroke({ color: P.snailBody, width: 4 });
  const hornTipL = new Graphics().circle(-40, -30, 3).fill(P.ink);
  const hornTipR = new Graphics().circle(-30, -32, 3).fill(P.ink);
  const shell = new Graphics().circle(6, -14, 26).fill(P.snailShell).stroke(OUTLINE);
  const spiral = new Graphics();
  spiral.moveTo(6, -14);
  for (let a = 0; a < Math.PI * 5; a += 0.25) {
    const r = 3 + a * 2.3;
    spiral.lineTo(6 + Math.cos(a) * r, -14 + Math.sin(a) * r);
  }
  spiral.stroke({ color: P.snailShellDark, width: 3, alpha: 0.7 });
  const smile = new Graphics().moveTo(-36, -2).quadraticCurveTo(-30, 3, -24, -2).stroke({ color: P.ink, width: 2, alpha: 0.5 });
  node.addChild(foot, hornL, hornR, hornTipL, hornTipR, shell, spiral, head, smile);
  eye(node, -33, -8, 4, 1);
  return { node, parts: { hornL, hornR, hornTipL, hornTipR } };
}

// --- Курица ----------------------------------------------------------
export function makeHen(): Sprite {
  const node = new Container();
  const tail = new Graphics()
    .moveTo(-26, -6)
    .quadraticCurveTo(-58, -30, -44, 6)
    .quadraticCurveTo(-54, -6, -26, 10)
    .fill(P.henBody).stroke(OUTLINE);
  const body = new Graphics().ellipse(0, 0, 40, 36).fill(P.henBody).stroke(OUTLINE);
  const belly = new Graphics().ellipse(4, 12, 26, 22).fill(P.henBelly);
  const wing = new Graphics()
    .moveTo(6, -8).quadraticCurveTo(-20, -6, -10, 22).quadraticCurveTo(6, 14, 14, -4)
    .fill(P.henLight).stroke(OUTLINE);
  const legs = new Graphics()
    .moveTo(-8, 32).lineTo(-8, 48).moveTo(-14, 48).lineTo(-2, 48)
    .moveTo(12, 32).lineTo(12, 48).moveTo(6, 48).lineTo(18, 48)
    .stroke({ color: P.beak, width: 4 });
  const head = new Graphics().circle(24, -26, 18).fill(P.henLight).stroke(OUTLINE);
  const comb = new Graphics()
    .circle(18, -44, 7).circle(27, -47, 7).circle(34, -42, 6)
    .fill(P.comb);
  const wattle = new Graphics().ellipse(24, -10, 5, 8).fill(P.comb);
  const beak = new Graphics().poly([40, -26, 56, -22, 40, -17]).fill(P.beak).stroke(OUTLINE);
  node.addChild(tail, legs, body, belly, wing, head, comb, wattle, beak);
  eye(node, 28, -28, 5, 1);
  return { node, parts: { wing, head } };
}

// --- Цыплёнок --------------------------------------------------------
export function makeChick(): Sprite {
  const node = new Container();
  const body = new Graphics().ellipse(0, 4, 16, 15).fill(P.chick).stroke(OUTLINE);
  const head = new Graphics().circle(0, -14, 13).fill(P.chick).stroke(OUTLINE);
  const wing = new Graphics().ellipse(-11, 4, 6, 9).fill(P.chickDark);
  const feet = new Graphics()
    .moveTo(-6, 18).lineTo(-6, 24).moveTo(-9, 24).lineTo(-2, 24)
    .moveTo(6, 18).lineTo(6, 24).moveTo(3, 24).lineTo(10, 24)
    .stroke({ color: P.beak, width: 3 });
  const beak = new Graphics().poly([9, -14, 20, -11, 9, -8]).fill(P.beak);
  const tear = new Graphics().ellipse(-8, -8, 3, 5).fill(P.waterLight);
  tear.visible = false;
  const zzz = new Text({ text: "z", style: { fontSize: 14, fill: P.bubbleInk, fontFamily: "Georgia, serif" } });
  zzz.position.set(10, -30);
  zzz.visible = false;
  node.addChild(feet, body, wing, head, beak, tear, zzz);
  eye(node, 2, -16, 4.5, 1);
  const shut = new Graphics().moveTo(-3, -15).lineTo(8, -15).stroke({ color: P.ink, width: 2.5 });
  shut.visible = false;
  node.addChild(shut);
  return { node, parts: { tear, zzz, shut } };
}

// --- Муха ----------------------------------------------------------
export function makeFly(): Sprite {
  const node = new Container();
  const wingL = new Graphics().ellipse(-10, -12, 13, 8).fill({ color: P.flyWing, alpha: 0.55 }).stroke({ color: P.ink, width: 1, alpha: 0.2 });
  const wingR = new Graphics().ellipse(10, -12, 13, 8).fill({ color: P.flyWing, alpha: 0.55 }).stroke({ color: P.ink, width: 1, alpha: 0.2 });
  const body = new Graphics().ellipse(0, 2, 12, 15).fill(P.fly).stroke(OUTLINE);
  const stripe = new Graphics().ellipse(0, 6, 10, 4).fill(0x22252c);
  const head = new Graphics().circle(0, -12, 8).fill(P.fly);
  const legs = new Graphics()
    .moveTo(-4, 14).lineTo(-10, 22).moveTo(0, 15).lineTo(0, 24).moveTo(4, 14).lineTo(10, 22)
    .stroke({ color: P.ink, width: 2, alpha: 0.6 });
  node.addChild(legs, wingL, wingR, body, stripe, head);
  eye(node, -4, -13, 4.5);
  eye(node, 5, -13, 4.5);
  return { node, parts: { wingL, wingR } };
}

// --- Яйцо ----------------------------------------------------------
export function makeEgg(): Sprite {
  const node = new Container();
  const glow = new Graphics().circle(0, -4, 30).fill({ color: P.sunCore, alpha: 0.0 });
  const shell = new Graphics()
    .moveTo(0, -30)
    .bezierCurveTo(20, -30, 22, 8, 0, 22)
    .bezierCurveTo(-22, 8, -20, -30, 0, -30)
    .fill(P.egg)
    .stroke(OUTLINE);
  const shade = new Graphics().ellipse(-6, 2, 8, 14).fill({ color: P.eggShade, alpha: 0.5 });
  const speck = new Graphics()
    .circle(6, -6, 2).circle(-3, -14, 1.6).circle(8, 8, 1.6)
    .fill({ color: P.eggShade, alpha: 0.8 });
  const crack = new Graphics();
  crack.visible = false;
  node.addChild(glow, shell, shade, speck, crack);
  return { node, parts: { glow, crack, shell } };
}

// --- Салат (кочан) ------------------------------------------------
export function makeSalad(): Sprite {
  const node = new Container();
  const outer = new Graphics()
    .ellipse(0, 0, 30, 26).fill(P.saladDark).stroke(OUTLINE);
  const mid = new Graphics().ellipse(0, -2, 24, 21).fill(P.salad);
  const leafA = new Graphics()
    .moveTo(-24, -2).quadraticCurveTo(-14, -26, 0, -20).quadraticCurveTo(-8, -6, -24, -2).fill(P.saladDark);
  const leafB = new Graphics()
    .moveTo(24, -2).quadraticCurveTo(14, -26, 0, -20).quadraticCurveTo(8, -6, 24, -2).fill(P.saladDark);
  const core = new Graphics().ellipse(0, -1, 12, 11).fill(P.saladCore);
  const veins = new Graphics()
    .moveTo(0, -12).lineTo(0, 10).moveTo(-10, -6).quadraticCurveTo(0, 0, 10, -6)
    .stroke({ color: P.saladCore, width: 2, alpha: 0.6 });
  node.addChild(outer, mid, leafA, leafB, core, veins);
  return { node, parts: {} };
}

// --- Прочее окружение --------------------------------------------
export function makeCloud(scale = 1): Container {
  const c = new Container();
  const g = new Graphics()
    .circle(-26, 4, 18).circle(-4, -6, 24).circle(20, 2, 20).circle(0, 12, 22).circle(-24, 14, 16).circle(24, 14, 14)
    .fill({ color: P.cloud, alpha: 0.95 });
  c.addChild(g);
  c.scale.set(scale);
  return c;
}

export function makeFlower(kind: 0 | 1 | 2): Container {
  const c = new Container();
  const petal = kind === 0 ? P.petalWhite : kind === 1 ? P.petalYellow : P.petalPink;
  c.addChild(new Graphics().moveTo(0, 0).lineTo(0, 16).stroke({ color: P.meadowShade, width: 3 }));
  const head = new Graphics();
  for (let i = 0; i < 6; i++) {
    const a = (i / 6) * Math.PI * 2;
    head.ellipse(Math.cos(a) * 6, Math.sin(a) * 6, 5, 7).fill(petal);
  }
  head.circle(0, 0, 4).fill(P.flowerCore);
  c.addChild(head);
  return c;
}

export function makeGrassTuft(): Graphics {
  return new Graphics()
    .moveTo(-6, 0).quadraticCurveTo(-8, -14, -2, -16)
    .moveTo(0, 0).quadraticCurveTo(0, -18, 4, -20)
    .moveTo(6, 0).quadraticCurveTo(10, -12, 12, -14)
    .stroke({ color: P.meadowShade, width: 3 });
}

export function makeRock(w: number, h: number): Graphics {
  return new Graphics()
    .moveTo(-w, h * 0.4)
    .quadraticCurveTo(-w, -h, 0, -h)
    .quadraticCurveTo(w, -h * 0.9, w, h * 0.3)
    .quadraticCurveTo(w * 0.5, h * 0.5, -w, h * 0.4)
    .fill(P.stone)
    .stroke(OUTLINE);
}

export function makeTree(): Container {
  const c = new Container();
  const trunk = new Graphics()
    .moveTo(-16, 0).quadraticCurveTo(-10, -80, -6, -120).lineTo(10, -120)
    .quadraticCurveTo(14, -70, 18, 0).fill(P.bark).stroke(OUTLINE);
  const canopy = new Graphics()
    .circle(-40, -150, 52).circle(30, -170, 60).circle(70, -120, 46).circle(0, -120, 50).circle(-30, -110, 40)
    .fill(P.leaf);
  const canopyHi = new Graphics()
    .circle(20, -180, 34).circle(-30, -160, 26)
    .fill(P.leafLight);
  const canopyLo = new Graphics().circle(50, -110, 30).circle(-40, -100, 24).fill(P.leafDark);
  c.addChild(trunk, canopyLo, canopy, canopyHi);
  return c;
}

export function makeWindmill(): Container {
  const c = new Container();
  const tower = new Graphics()
    .poly([-16, 0, 16, 0, 10, -46, -10, -46])
    .fill(P.woodLight).stroke(OUTLINE);
  const cap = new Graphics().poly([-13, -46, 13, -46, 0, -62]).fill(P.woodDark);
  const bladeC = new Container();
  for (let i = 0; i < 4; i++) {
    const b = new Graphics().poly([0, -3, 44, -8, 46, 4, 4, 4]).fill(P.woodDark);
    b.rotation = (i / 4) * Math.PI * 2;
    bladeC.addChild(b);
  }
  bladeC.position.set(0, -50);
  c.addChild(tower, cap, bladeC);
  return c;
}

export function makeCottage(roof: number): Container {
  const c = new Container();
  const wall = new Graphics().rect(-20, -24, 40, 24).fill(P.woodLight).stroke(OUTLINE);
  const roofG = new Graphics().poly([-26, -24, 26, -24, 0, -46]).fill(roof).stroke(OUTLINE);
  const win = new Graphics().rect(-6, -18, 12, 12).fill(P.sunCore);
  c.addChild(wall, roofG, win);
  (c as any).win = win;
  return c;
}

export function makeBirdhouse(): Container {
  const c = new Container();
  const back = new Graphics().roundRect(-40, -46, 80, 78, 8).fill(P.woodDark).stroke(OUTLINE);
  const front = new Graphics().roundRect(-34, -40, 68, 66, 6).fill(P.wood).stroke(OUTLINE);
  const roofL = new Graphics().poly([-44, -38, 0, -74, 4, -70, -38, -32]).fill(P.woodDark);
  const roofR = new Graphics().poly([44, -38, 0, -74, -4, -70, 38, -32]).fill(P.woodDark);
  const hole = new Graphics().circle(0, -6, 22).fill(0x2b1c0e);
  const perch = new Graphics().circle(0, 24, 5).fill(P.woodGrain);
  c.addChild(back, front, roofL, roofR, hole, perch);
  return c;
}

export function makeLilyPad(r: number): Graphics {
  return new Graphics()
    .moveTo(0, 0)
    .arc(0, 0, r, 0.5, Math.PI * 2 + 0.2)
    .lineTo(0, 0)
    .fill(P.lily)
    .stroke({ color: P.saladDark, width: 2, alpha: 0.5 });
}

export function makeReeds(): Graphics {
  const g = new Graphics();
  for (let i = 0; i < 5; i++) {
    const x = i * 10 - 20;
    g.moveTo(x, 0).quadraticCurveTo(x - 4, -40, x + 2, -60).stroke({ color: P.meadowShade, width: 4 });
    g.ellipse(x + 2, -62, 4, 10).fill(P.dirt);
  }
  return g;
}
